import { Component, type ErrorInfo, type ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { Button } from "@/components/ui/button";
import {
  isChunkLoadError,
  reloadForChunkLoadRecovery,
  shouldReloadForChunkLoadError,
} from "@/lib/chunk-load-error";

type Props = {
  children: ReactNode;
};

type State = {
  hasError: boolean;
  error?: Error;
  chunkLoadRecoveryExhausted?: boolean;
};

export class AppErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const chunkLoadError = isChunkLoadError(error);
    const shouldReload = shouldReloadForChunkLoadError(error);

    if (chunkLoadError) {
      if (shouldReload) {
        Sentry.addBreadcrumb({
          category: "chunk-load-recovery",
          level: "info",
          message: "Reloading after a stale application asset",
          data: { recovery: "reload" },
        });
        reloadForChunkLoadRecovery();
        return;
      }

      Sentry.captureException(error, {
        tags: {
          "error.kind": "stale_chunk_load",
          recovery: "exhausted",
        },
        fingerprint: ["stale_chunk_load", "recovery_exhausted"],
        contexts: {
          chunk_load: {
            recovery: "exhausted",
          },
        },
        extra: { componentStack: info.componentStack },
      });
      console.error("Stale application asset recovery exhausted");
      this.setState({ chunkLoadRecoveryExhausted: true });
      return;
    }

    Sentry.captureException(error, {
      extra: { componentStack: info.componentStack },
    });
    console.error("Uncaught error", error, info);
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: undefined,
      chunkLoadRecoveryExhausted: false,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const showChunkReloadMessage =
        this.state.chunkLoadRecoveryExhausted &&
        isChunkLoadError(this.state.error);

      return (
        <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-4">
          <div className="max-w-md text-center space-y-4">
            <h1 className="text-2xl font-semibold">
              {showChunkReloadMessage
                ? "Anwendung aktualisiert"
                : "Etwas ist schiefgelaufen"}
            </h1>
            <p className="text-muted-foreground">
              {showChunkReloadMessage
                ? "Die Anwendung wurde aktualisiert. Bitte laden Sie die Seite neu."
                : this.state.error?.message ??
                  "Unbekannter Fehler. Bitte laden Sie die Seite neu oder versuchen Sie es später erneut."}
            </p>
            <div className="flex gap-2 justify-center">
              {!showChunkReloadMessage && (
                <Button variant="outline" onClick={this.handleRetry}>
                  Weiterarbeiten
                </Button>
              )}
              <Button onClick={this.handleReload}>Seite neu laden</Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
